import { useRef, useState } from "react";

let loading;
const loadCheckout = () => {
  if (window.Razorpay) return Promise.resolve();
  if (!loading) loading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = process.env.REACT_APP_RAZORPAY_CHECKOUT_URL;
    const timer = setTimeout(() => { loading = null; script.remove(); reject(new Error("Checkout took too long to load. Try again.")); }, 15000);
    script.onload = () => { clearTimeout(timer); resolve(); };
    script.onerror = () => { clearTimeout(timer); loading = null; script.remove(); reject(new Error("Checkout could not load. Check your connection and try again.")); };
    document.body.appendChild(script);
  });
  return loading;
};

export const usePaymentCheckout = () => {
  const [busy, setBusy] = useState(""), [stage, setStage] = useState(""), [error, setError] = useState(""), [pending, setPending] = useState(false);
  const locked = useRef(false), verification = useRef(null), phase = useRef("");
  const verify = async () => {
    if (!verification.current || phase.current === "verifying") return;
    phase.current = "verifying"; setStage("Confirming payment…"); setError(""); setPending(false);
    try {
      await verification.current(); verification.current = null; locked.current = false;
      phase.current = ""; setBusy(""); setStage("Payment confirmed");
    } catch {
      phase.current = "pending"; setPending(true); setStage("Payment needs verification");
      setError("Your payment may have gone through. Don’t pay again—check its status below.");
    }
  };
  const open = async ({ id, createOrder, verifyPayment, name, description, user }) => {
    if (locked.current) return;
    locked.current = true; phase.current = "opening"; setBusy(id); setStage("Opening secure checkout…"); setError("");
    try {
      await loadCheckout(); const order = await createOrder();
      const checkout = new window.Razorpay({ key: order.key_id, order_id: order.order_id, amount: order.amount, currency: order.currency,
        name, description: description(order), prefill: { name: user?.name, email: user?.email }, theme: { color: "#D2378B" },
        handler: response => { if (verification.current) return; verification.current = () => verifyPayment(response); verify(); },
        modal: { ondismiss: () => { if (verification.current) return; locked.current = false; phase.current = ""; setBusy(""); setError(""); setStage("Checkout closed. You can try again when ready."); } } });
      checkout.on("payment.failed", () => { setError("Payment attempt failed. Try another available method in checkout, or close it to retry later."); });
      phase.current = "checkout"; setStage("Complete payment in secure checkout"); checkout.open();
    } catch (e) {
      locked.current = false; phase.current = ""; setBusy(""); setStage("");
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : e.message || "Checkout is unavailable. Please try again.");
    }
  };
  return { busy, stage, error, pending, open, retry: verify };
};

export const CheckoutStatus = ({ checkout, prefix }) => (
  <div className="space-y-3" data-testid={`${prefix}-checkout-status`}>
    {checkout.stage && <p role="status" aria-live="polite" data-testid={`${prefix}-checkout-stage`} className="text-sm">{checkout.stage}</p>}
    {checkout.error && <p role="alert" data-testid={`${prefix}-checkout-error`} className="text-sm text-traffic-red">{checkout.error}</p>}
    {checkout.pending && <button type="button" className="border border-foreground rounded-lg px-4 py-2 text-sm hover:bg-surface transition-colors" data-testid={`${prefix}-verify-retry`} onClick={checkout.retry}>Check payment status—don’t pay again</button>}
  </div>
);
